import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import useReducedMotion from '../hooks/useReducedMotion';
import useScrollLock from '../hooks/useScrollLock';
import './menuDrawer.css';

export default function MenuDrawer({ onClose, triggerRef }) {
  const root = useRef(null);
  const closeRef = useRef(() => {});
  const reduced = useReducedMotion();
  useScrollLock(true);
  useLayoutEffect(() => {
    let closing = false;
    const drawer = root.current;
    const trigger = triggerRef.current;
    let entrance;
    const context = gsap.context(() => {
      entrance = gsap.timeline({ defaults: { ease: 'power4.inOut' } })
        .from('.menu-drawer__backdrop', { opacity: 0, duration: reduced ? 0 : .4 }, 0)
        .from('.menu-drawer__layer', { xPercent: 100, duration: reduced ? 0 : .7 }, 0)
        .from('.menu-drawer__panel', { xPercent: 100, duration: reduced ? 0 : .75 }, reduced ? 0 : .09)
        .from('.menu-drawer__link > span', { yPercent: 120, rotate: reduced ? 0 : 5, duration: reduced ? 0 : .75, stagger: reduced ? 0 : .12 }, reduced ? 0 : .38)
        .from('.menu-drawer__eyebrow, .menu-drawer__footer', { autoAlpha: 0, y: reduced ? 0 : 12, duration: reduced ? 0 : .6 }, reduced ? 0 : .5);
    }, root);
    drawer.querySelector('.menu-drawer__close').focus({ preventScroll: true });
    context.add('close', (destination) => {
      if (closing) return;
      closing = true;
      entrance.kill();
      gsap.timeline({ onComplete: () => {
        onClose(destination);
        requestAnimationFrame(() => {
          if (!destination) trigger?.focus({ preventScroll: true });
        });
      } })
        .to('.menu-drawer__link > span', { yPercent: reduced ? 0 : -110, autoAlpha: 0, duration: reduced ? 0 : .24, stagger: reduced ? 0 : .04 })
        .to('.menu-drawer__panel', { xPercent: reduced ? 0 : 100, autoAlpha: reduced ? 0 : 1, duration: reduced ? 0 : .6, ease: 'power4.inOut' }, reduced ? 0 : .08)
        .to('.menu-drawer__layer', { xPercent: reduced ? 0 : 100, autoAlpha: reduced ? 0 : 1, duration: reduced ? 0 : .6, ease: 'power4.inOut' }, reduced ? 0 : .17)
        .to('.menu-drawer__backdrop', { opacity: 0, duration: reduced ? 0 : .4 }, reduced ? 0 : .25);
    });
    closeRef.current = context.close;
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); context.close(); }
      if (event.key === 'Tab') {
        const items = [...drawer.querySelectorAll('button, a[href]')];
        const first = items[0]; const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { context.revert(); document.removeEventListener('keydown', onKey); };
  }, [onClose, triggerRef, reduced]);

  return createPortal(
    <div className="menu-drawer" ref={root}>
      <div className="menu-drawer__backdrop" onClick={() => closeRef.current()} aria-hidden="true" />
      <div className="menu-drawer__layer" aria-hidden="true" />
      <div className="menu-drawer__panel" id="header-navigation" role="dialog" aria-modal="true" aria-label="Main navigation">
        <button className="menu-drawer__close" type="button" onClick={() => closeRef.current()} aria-label="Close navigation">
          <span /><span />
        </button>
        <p className="menu-drawer__eyebrow">A LITTLE EXPLORING?</p>
        <nav aria-label="Main navigation">
          {[['HOME', '/'], ['PROJECTS', '/projects'], ['SERVICES', '/#services'], ['ABOUT ME', '/about'], ['CONTACT', '#contact']].map(([label, target], index) => (
            <a className="menu-drawer__link" href={target} key={target} onClick={(event) => { event.preventDefault(); closeRef.current(target); }}>
              <span>{label}<sup>0{index + 1}</sup></span>
            </a>
          ))}
        </nav>
        <div className="menu-drawer__footer"><span>ZEINA TAMER</span><p>Driven by psychology.<br />Designed for impact.</p><span className="menu-drawer__spark" aria-hidden="true">✳︎</span></div>
      </div>
    </div>, document.body,
  );
}
